'use client';

import type { ReactNode } from 'react';
import Image from 'next/image';
import { Box, Typography, type TypographyProps } from '@/primitives';

type ErrorCowCardProps = {
  /** Small uppercase badge rendered above the title, e.g. "404 · Animal not found". */
  label?: string;
  titleId?: string;
  titleComponent?: TypographyProps['component'];
  title: string;
  description: ReactNode;
  /** Action buttons rendered below the description. */
  actions?: ReactNode;
};

export function ErrorCowCard({
  label,
  titleId,
  titleComponent = 'h1',
  title,
  description,
  actions,
}: ErrorCowCardProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: '400px',
        // A landscape phone leaves ~290px between the nav and the footer, so
        // the 400px floor would push the actions past the bottom of a
        // container that clips (PublicErrorSection). Drop the floor and let
        // the card scroll instead of hiding what the user has to click.
        '@media (max-height: 600px)': {
          minHeight: 0,
          maxHeight: '100%',
          overflowY: 'auto',
          justifyContent: 'flex-start',
        },
        padding: { xs: '16px', sm: '32px' },
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '520px',
          padding: { xs: '20px', sm: '32px' },
          borderRadius: '12px',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
            // Sized here rather than inline so the short-viewport override
            // below can win — an inline style would outrank it.
            '& img': { width: '100%', maxWidth: '200px', height: 'auto' },
            '@media (max-height: 600px)': {
              mb: 1,
              '& img': { maxWidth: '120px' },
            },
          }}
        >
          <Image
            src="/images/error-cow.png"
            alt="Friendly cow illustration"
            width={200}
            height={150}
            priority
          />
        </Box>
        {label && (
          <Typography
            variant="labelsmall"
            sx={{
              display: 'block',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'primary.main',
              fontWeight: 600,
              mb: 1,
            }}
          >
            {label}
          </Typography>
        )}
        <Typography
          id={titleId}
          component={titleComponent}
          variant="headlinelarge"
          sx={{ color: 'text.primary', mb: 1 }}
        >
          {title}
        </Typography>
        <Typography
          variant="bodymedium"
          component="div"
          sx={{ color: 'text.secondary', mb: actions ? 3 : 0, lineHeight: 1.6 }}
        >
          {description}
        </Typography>
        {actions}
      </Box>
    </Box>
  );
}
